import React from 'react';
import { ArrowRight, Check, Lock } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { LucideIcon } from './AurexSections';

// ─────────────────────────────────────────────────────────────────────────
// ModernAudience — the «Moderno» theme Target Audience (s139, approved by
// Anthony: "Statement Band"). Graphite band: the subtitle (or title) as a big
// serif statement; below, the cards — a description written as several lines
// (or "→"-separated phrases) renders as a ✓ checklist; an item with the "lock"
// icon renders as the access note next to the CTA.
// Data = aurex_audience (eyebrow/title/subtitle/CTA + items icon/title/description).
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const PAD = 'px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';

// Rich text → list of phrases (lines, list items, or "→"-separated).
function toLines(html) {
  const div = document.createElement('div');
  div.innerHTML = String(html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|li|div|h\d)>/gi, '\n');
  return (div.textContent || '').split(/\n|→/).map(s => s.replace(/ /g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
}
const isNote = (i) => /^(lock|shield|shield-check|key)$/i.test(String(i.icon || ''));

function Title({ text, dark }) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return <>{text}</>;
  const last = words.pop();
  return <>{words.join(' ')} <em style={{ fontStyle: 'italic', fontWeight: 500, color: dark ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}>{last}</em></>;
}

function Eyebrow({ text, dark }) {
  if (!text) return null;
  return (
    <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: dark ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}>
      <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{text}
    </div>
  );
}

function Cta({ config }) {
  const tt = useT();
  const text = tt(config.cta_text);
  if (!text || !config.cta_url) return null;
  return (
    <a href={config.cta_url} target={config.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" data-testid="audience-cta"
      className="inline-flex items-center gap-2 self-start font-semibold text-sm px-6 py-3.5 rounded-[2px] mx-btn mx-btn--primary mx-dark"
      style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }}>
      {text} <ArrowRight className="w-4 h-4" />
    </a>
  );
}

function Checklist({ lines, dark }) {
  return (
    <ul className="flex flex-col gap-3.5">
      {lines.map((l, k) => (
        <li key={k} className="grid grid-cols-[28px_1fr] gap-3 items-start">
          <span className="grid place-items-center w-7 h-7 rounded-full" style={{ background: dark ? 'color-mix(in srgb, var(--color-accent, #ff9c1b) 18%, transparent)' : 'color-mix(in srgb, var(--color-accent, #ff9c1b) 16%, #fff)', color: dark ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}><Check className="w-4 h-4" strokeWidth={2.6} /></span>
          <span className="text-base md:text-[17px] leading-snug" style={{ color: dark ? 'rgba(244,241,234,.92)' : 'var(--color-heading, #1a2332)' }}>{l}</span>
        </li>
      ))}
    </ul>
  );
}

function NoteCard({ item, dark }) {
  const tt = useT();
  const lines = toLines(normalizeRichText(tt(item.description)));
  return (
    <div className="grid grid-cols-[40px_1fr] gap-4 p-6 rounded-[3px]" style={{ background: dark ? 'rgba(255,255,255,.05)' : '#16171a', border: dark ? '1px solid rgba(255,255,255,.12)' : 'none' }} data-testid={`audience-card-${item.id}`}>
      <span className="grid place-items-center w-10 h-10 rounded-full" style={{ border: '1px solid color-mix(in srgb, var(--color-accent, #ff9c1b) 50%, transparent)', color: 'var(--color-accent, #ff9c1b)' }}><Lock className="w-4 h-4" /></span>
      <div>
        <div className="font-semibold text-lg" style={{ fontFamily: SERIF, color: '#f4f1ea' }}>{tt(item.title)}</div>
        {lines.length > 0 && <p className="mt-1.5 text-sm leading-relaxed" style={{ color: 'rgba(244,241,234,.72)' }}>{lines.join(' ')}</p>}
      </div>
    </div>
  );
}

function ItemBlock({ item, dark }) {
  const tt = useT();
  if (isNote(item)) return <NoteCard item={item} dark={dark} />;
  const html = normalizeRichText(tt(item.description));
  const lines = toLines(html);
  return (
    <div className="flex flex-col gap-4" data-testid={`audience-card-${item.id}`}>
      <div className="flex items-center gap-3">
        {!dark && item.icon && <span style={{ color: 'var(--accent-strong, #b86500)' }}><LucideIcon name={item.icon} className="w-5 h-5" /></span>}
        <h3 className="font-semibold" style={{ fontFamily: SERIF, fontSize: dark ? '1.35rem' : '1.6rem', lineHeight: 1.2, color: dark ? '#f4f1ea' : 'var(--color-heading, #1a2332)' }}>{tt(item.title)}</h3>
      </div>
      {lines.length > 1
        ? <Checklist lines={lines} dark={dark} />
        : (lines.length === 1 && <div className="rich-text-content text-base leading-relaxed" style={{ color: dark ? 'rgba(244,241,234,.72)' : 'var(--color-body-text, #475569)' }} dangerouslySetInnerHTML={{ __html: html }} />)}
    </div>
  );
}

function StatementBand({ config, items }) {
  const tt = useT();
  const statement = tt(config.subtitle) || tt(config.title);
  const main = items.filter(i => !isNote(i));
  const notes = items.filter(isNote);
  return (
    <section id="aurex-audience" data-testid="aurex-section-audience" style={{ background: '#16171a' }}>
      <div className={`max-w-[1440px] mx-auto ${PAD} py-20 lg:py-28`}>
        <div className="flex flex-col gap-5 max-w-4xl">
          <Eyebrow text={tt(config.eyebrow) || tt(config.title)} dark />
          {statement && <h2 className="font-semibold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 4.2vw, 3.75rem)', lineHeight: 1.08, color: '#f4f1ea', textWrap: 'balance' }} data-testid="audience-title"><Title text={statement} dark /></h2>}
        </div>
        <div className="mt-12 pt-10 border-t grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-10 lg:gap-16" style={{ borderColor: 'rgba(255,255,255,.14)' }}>
          <div className="flex flex-col gap-10">{main.map(i => <ItemBlock key={i.id} item={i} dark />)}</div>
          <div className="flex flex-col gap-6">
            {notes.map(i => <NoteCard key={i.id} item={i} dark />)}
            <Cta config={config} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ModernAudience({ config = {}, items = [] }) {
  const { lang } = useLang();
  const visible = (items || []).filter(i => i.visible !== false && itemHasLocale(i.title, lang));
  if (!visible.length) return null;
  return <StatementBand config={config} items={visible} />;
}
